/**
 * Authentication Service
 * 
 * Handles JWT authentication with the Django backend:
 * - Login/Logout
 * - Token management (access & refresh tokens)
 * - Automatic token refresh
 * - User session management
 * 
 * SSO Support: Uses the same localStorage keys as the main frontend
 * to enable seamless authentication between valpha.io and ai.valpha.io
 */

import axios from 'axios';

// Use the same pattern as the main frontend: host from env + /api suffix
const API_BASE_URL = `${import.meta.env.VITE_API_BASE_URL || ''}/api`;

// Token storage keys - MUST match main frontend for SSO
// Main frontend uses 'token' for access token
const ACCESS_TOKEN_KEY = 'token'; // Changed from 'auth_token' to match main frontend
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_KEY = 'user';

// Legacy key from previous versions (for migration)
const LEGACY_ACCESS_TOKEN_KEY = 'auth_token';

// Types
export interface LoginCredentials {
  username: string;
  password: string;
}

export interface LoginResponse {
  access: string;
  refresh: string;
}

export interface User {
  id: number;
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
  is_staff?: boolean;
  is_superuser?: boolean;
  is_active?: boolean;
  date_joined?: string;
}

export interface RegisterData {
  username: string;
  email: string;
  password: string;
  password2?: string;
  first_name?: string;
  last_name?: string;
}

/**
 * Login with username and password
 */
export const login = async (credentials: LoginCredentials): Promise<User> => {
  try {
    // Get JWT tokens
    const tokenResponse = await axios.post<LoginResponse>(
      `${API_BASE_URL}/token/`,
      credentials
    );

    const { access, refresh } = tokenResponse.data;

    // Store tokens
    localStorage.setItem(ACCESS_TOKEN_KEY, access);
    localStorage.setItem(REFRESH_TOKEN_KEY, refresh);

    // Fetch user profile
    const userResponse = await axios.get<User>(`${API_BASE_URL}/accounts/me/`, {
      headers: {
        Authorization: `Bearer ${access}`,
      },
    });

    const user = userResponse.data;
    localStorage.setItem(USER_KEY, JSON.stringify(user));

    return user;
  } catch (error) {
    // Clear any partial data
    logout();
    throw error;
  }
};

/**
 * Register a new user
 */
export const register = async (data: RegisterData): Promise<User> => {
  try {
    // Register user
    await axios.post(
      `${API_BASE_URL}/accounts/register/`,
      data
    );

    // Auto-login after registration
    return await login({
      username: data.username,
      password: data.password,
    });
  } catch (error) {
    throw error;
  }
};

/**
 * Logout and clear all stored data
 */
export const logout = (): void => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  // Also remove legacy key if present
  localStorage.removeItem(LEGACY_ACCESS_TOKEN_KEY);
};

/**
 * Get current access token
 * Checks main key first, then legacy key for migration
 */
export const getAccessToken = (): string | null => {
  // Check main key first (shared with main frontend)
  let token = localStorage.getItem(ACCESS_TOKEN_KEY);
  
  // If not found, check legacy key and migrate
  if (!token) {
    token = localStorage.getItem(LEGACY_ACCESS_TOKEN_KEY);
    if (token) {
      // Migrate to new key
      localStorage.setItem(ACCESS_TOKEN_KEY, token);
      localStorage.removeItem(LEGACY_ACCESS_TOKEN_KEY);
    }
  }
  
  return token;
};

/**
 * Get current refresh token
 */
export const getRefreshToken = (): string | null => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

/**
 * Get stored user data
 */
export const getUser = (): User | null => {
  const userJson = localStorage.getItem(USER_KEY);
  if (!userJson) return null;

  try {
    return JSON.parse(userJson);
  } catch {
    return null;
  }
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  return !!getAccessToken();
};

/**
 * Refresh the access token using the refresh token
 */
export const refreshAccessToken = async (): Promise<string> => {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  try {
    const response = await axios.post<{ access: string }>(
      `${API_BASE_URL}/token/refresh/`,
      { refresh: refreshToken }
    );

    const { access } = response.data;
    localStorage.setItem(ACCESS_TOKEN_KEY, access);

    return access;
  } catch (error) {
    // If refresh fails, user needs to login again
    logout();
    throw error;
  }
};

/**
 * Decode JWT token to check expiration
 * Returns the token payload or null if invalid
 */
export const decodeToken = (token: string): any => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

/**
 * Check if token is expired or about to expire (within 60 seconds)
 */
export const isTokenExpired = (token: string): boolean => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;

  const expirationTime = decoded.exp * 1000; // Convert to milliseconds
  const currentTime = Date.now();
  const bufferTime = 60 * 1000; // 60 seconds buffer

  return expirationTime - currentTime < bufferTime;
};

/**
 * Get user profile from backend
 */
export const fetchUserProfile = async (): Promise<User> => {
  const token = getAccessToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const response = await axios.get<User>(`${API_BASE_URL}/accounts/me/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const user = response.data;
  localStorage.setItem(USER_KEY, JSON.stringify(user));

  return user;
};

export const authService = {
  login,
  register,
  logout,
  decodeToken,
  getAccessToken,
  getRefreshToken,
  getUser,
  isAuthenticated,
  refreshAccessToken,
  isTokenExpired,
  fetchUserProfile,
};

export default authService;
